import { useEffect, useMemo, useRef, useState } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

const MODULES = [
  { key: "dashboard",       label: "Dashboard",       actions: ["read"] },
  { key: "usuarios",        label: "Usuarios",        actions: ["read", "create", "update", "delete"] },
  { key: "sellos",          label: "Sellos",          actions: ["read", "create", "update", "delete"] },
  { key: "generos",         label: "Géneros",         actions: ["read", "create", "update", "delete"] },
  { key: "grupos",          label: "Grupos",          actions: ["read", "create", "update", "delete"] },
  { key: "nacionalidades",  label: "Nacionalidades",  actions: ["read", "create", "update", "delete"] },
  { key: "reconocimientos", label: "Reconocimientos", actions: ["read", "create", "update", "delete"] },
  { key: "parametros",      label: "Parámetros",      actions: ["read", "create", "update", "delete"] },
  { key: "certificaciones", label: "Certificaciones", actions: ["read", "create", "update", "delete"] },
  { key: "reportes",        label: "Reportes",        actions: ["read"] },
];

const keyOf = (m, a) => `${m}.${a}`;

const equalSetToArray = (set, arr = []) => {
  if (!(set instanceof Set)) return false;
  if (!Array.isArray(arr)) return false;
  if (set.size !== arr.length) return false;
  for (const x of arr) if (!set.has(x)) return false;
  return true;
};

export default function PermissionUser({ value = [], onChange }) {
  // estado local
  const [setPerms, setSetPerms] = useState(() => new Set(value));

  // 🔁 Sync cuando cambian permisos desde el padre (editar usuario)
  useEffect(() => {
    if (!equalSetToArray(setPerms, value)) {
      setSetPerms(new Set(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 📨 Emitir cambios al padre SOLO cuando cambie el estado local
  const lastEmitted = useRef(value);

  useEffect(() => {
    const arr = Array.from(setPerms);
    if (!equalSetToArray(setPerms, lastEmitted.current)) {
      lastEmitted.current = arr;
      onChange?.(arr);
    }
  }, [setPerms, onChange]);

  // ✅ toggles SOLO actualizan estado local
  const toggle = (k) =>
    setSetPerms((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const toggleModule = (modKey, actions, nextChecked) =>
    setSetPerms((prev) => {
      const next = new Set(prev);
      actions.forEach((a) => {
        const k = keyOf(modKey, a);
        nextChecked ? next.add(k) : next.delete(k);
      });
      return next;
    });

  const moduleState = useMemo(() => {
    const m = {};
    MODULES.forEach(({ key, actions }) => {
      const total = actions.length;
      const on = actions.filter((a) => setPerms.has(keyOf(key, a))).length;
      m[key] = { total, on, all: on === total, some: on > 0 && on < total };
    });
    return m;
  }, [setPerms]);

  const actionLabel = (a) =>
    a === "read" ? "Ver" : a === "create" ? "Crear" : a === "update" ? "Editar" : "Eliminar";

  return (
    <Card sx={{ p: 3 }}>
      <SoftTypography variant="h6" fontWeight="medium" gutterBottom>
        Permisos
      </SoftTypography>

      <Grid container spacing={2}>
        {MODULES.map(({ key, label, actions }) => {
          const st = moduleState[key] || { all: false, some: false };
          return (
            <Grid item xs={12} key={key}>
              <SoftBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <SoftTypography variant="button" fontWeight="bold">
                  {label}
                </SoftTypography>

                <FormControlLabel
                  label={st.some && !st.all ? "Parcial" : st.all ? "Todos" : "Ninguno"}
                  control={
                    <Switch
                      checked={!!st.all}
                      onChange={(e) => toggleModule(key, actions, e.target.checked)}
                    />
                  }
                />
              </SoftBox>

              <SoftBox display="flex" gap={3} flexWrap="wrap" pl={1} mb={1}>
                {actions.map((a) => {
                  const permKey = keyOf(key, a);
                  return (
                    <FormControlLabel
                      key={permKey}
                      label={actionLabel(a)}
                      control={
                        <Switch
                          checked={setPerms.has(permKey)}
                          onChange={() => toggle(permKey)}
                        />
                      }
                    />
                  );
                })}
              </SoftBox>
              <Divider sx={{ my: 1 }} />
            </Grid>
          );
        })}
      </Grid>
    </Card>
  );
}
